"use client"

import { useState } from 'react'
import type { DailyTrafficPoint } from './types'

const formatDay = (day: string) =>
  new Date(`${day}T00:00:00Z`).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  })

// visitors / pageviews, i.e. how much of the traffic was distinct people vs.
// repeat views. Guards the divide-by-zero on empty days.
const visitorShare = (pageviews: number, visitors: number) =>
  pageviews > 0 ? (visitors / pageviews) * 100 : 0

// "Nice" round y-axis ticks: the smallest 1/2/5×10^k step that yields at most
// four gridlines, then every multiple of it up to the data max. Counts are
// integers, so the step never drops below 1.
const yAxisTicks = (max: number) => {
  const rough = max / 4
  const pow = 10 ** Math.max(0, Math.floor(Math.log10(rough)))
  const step = [1, 2, 5, 10].map(m => m * pow).find(s => s >= rough) ?? 10 * pow
  const ticks: number[] = []
  for (let tick = step; tick <= max; tick += step) ticks.push(tick)
  return ticks
}

// Compact tick labels ("500", "1.5k") so the axis stays quiet. Tick values are
// 1/2/5×10^k multiples, so the k-division never produces long decimals.
const formatTick = (value: number) =>
  value >= 1000 ? `${value / 1000}k` : `${value}`

// Hand-rolled two-tone bar chart: each day is a flex column whose full bar is
// pageviews (light tint) with the unique-visitors share layered inside it
// (solid accent). A minimal y-axis (rotated title, round-number ticks, faint
// gridlines) gives the bars a readable scale. Heights and tick positions are
// computed from the data, so they stay inline styles; everything static is
// Tailwind.
export const DailyBarChart = ({ daily }: { daily: DailyTrafficPoint[] }) => {
  // Index of the bar the pointer is over, driving a styled tooltip. The native
  // `title` attribute was slow to appear and plain — this replaces it.
  const [hovered, setHovered] = useState<number | null>(null)

  if (daily.length === 0) return null
  const max = Math.max(1, ...daily.map(point => point.pageviews))
  const totalPageviews = daily.reduce((sum, point) => sum + point.pageviews, 0)
  const totalVisitors = daily.reduce((sum, point) => sum + point.visitors, 0)
  const overallShare = visitorShare(totalPageviews, totalVisitors)

  const ticks = yAxisTicks(max)

  return (
    <div className="flex">
      {/* Rotated axis title, reading bottom-to-top like the axis it labels. */}
      <span className="flex h-64 w-4 rotate-180 items-center justify-center text-[10px] tracking-wide text-[#999] [writing-mode:vertical-rl]">
        count / day
      </span>
      {/* Tick-value gutter: each label vertically centered on its gridline. */}
      <div className="relative h-64 w-8 text-[10px] leading-none tabular-nums text-[#999]">
        {ticks.map(tick => (
          <span
            key={tick}
            className="absolute right-1.5 translate-y-1/2"
            style={{ bottom: `${(tick / max) * 100}%` }}
          >
            {formatTick(tick)}
          </span>
        ))}
      </div>
      <div className="min-w-0 flex-1">
        <div className="relative flex h-64 items-end gap-px border-b border-l border-neutral-300">
          {/* Gridlines first in DOM order so the bars paint over them. */}
          {ticks.map(tick => (
            <div
              key={tick}
              className="absolute inset-x-0 border-t border-neutral-200"
              style={{ bottom: `${(tick / max) * 100}%` }}
            />
          ))}
          {daily.map((point, index) => (
            <div
              key={point.day}
              className="group relative h-full min-w-0 flex-1 cursor-default"
              onMouseEnter={() => setHovered(index)}
              onMouseLeave={() => setHovered(current => (current === index ? null : current))}
            >
              {/* Hover hit-area highlight so the whole column, not just the bar,
                  signals which day the tooltip describes. */}
              <div className="absolute inset-0 bg-[#2A623D]/0 group-hover:bg-[#2A623D]/5" />
              <div
                className="absolute inset-x-0 bottom-0 bg-[#2A623D]/25"
                style={{ height: `${(point.pageviews / max) * 100}%` }}
              />
              <div
                className="absolute inset-x-0 bottom-0 bg-[#2A623D]"
                style={{ height: `${(point.visitors / max) * 100}%` }}
              />
              {hovered === index && (
                <div
                  className={`pointer-events-none absolute bottom-full z-10 mb-1.5 w-max max-w-[180px] whitespace-normal border border-neutral-700 bg-neutral-900 px-2 py-1.5 text-left text-[11px] leading-snug text-white shadow-lg ${
                    // Flip anchoring near the edges so the card stays on-screen.
                    index < daily.length / 2
                      ? 'left-0'
                      : 'right-0'
                  }`}
                >
                  <div className="font-semibold">{formatDay(point.day)}</div>
                  <div className="mt-0.5 flex items-center justify-between gap-3 tabular-nums">
                    <span className="flex items-center gap-1">
                      {/* The pageview bar is #2A623D at 25% over white; this is
                          that resolved pale sage, kept solid so it's visible on
                          the dark tooltip. */}
                      <span className="inline-block h-2 w-2 bg-[#c9d7ce]" />
                      pageviews
                    </span>
                    <span>{point.pageviews.toLocaleString()}</span>
                  </div>
                  <div className="flex items-center justify-between gap-3 tabular-nums">
                    <span className="flex items-center gap-1">
                      <span className="inline-block h-2 w-2 bg-[#2A623D]" />
                      visitors
                    </span>
                    <span>{point.visitors.toLocaleString()}</span>
                  </div>
                  <div className="mt-0.5 border-t border-neutral-700 pt-0.5 text-[10px] text-neutral-300">
                    {visitorShare(point.pageviews, point.visitors).toFixed(0)}% unique
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
        <div className="mt-1 flex items-baseline justify-between text-[11px] text-[#666]">
          <span>{formatDay(daily[0].day)}</span>
          <span className="flex items-center gap-3">
            <span className="flex items-center gap-1">
              <span className="inline-block h-2 w-2 bg-[#2A623D]/25" />
              pageviews
            </span>
            <span className="flex items-center gap-1">
              <span className="inline-block h-2 w-2 bg-[#2A623D]" />
              visitors
            </span>
            <span className="text-[#2A623D]">{overallShare.toFixed(0)}% unique</span>
          </span>
          <span>{formatDay(daily[daily.length - 1].day)}</span>
        </div>
      </div>
    </div>
  )
}
